// Plans a fully historical match: timestamps for the four half-boundaries and
// a randomized list of in-play events (goals, cards, subs) spread across the
// two halves. Pure / deterministic when given a seeded RNG so it's testable.
//
// The output is an ordered sequence of {title, payload} pairs ready to feed
// straight into the live-event POST loop. Half-boundary events are interleaved
// in the right places so the backend sees them in chronological order.

import {
  buildKickOffEvent,
  buildHalfEndEvent,
  buildHalfTimeEvent,
  buildSecondHalfKickOffEvent,
  buildSecondHalfEndEvent,
  buildGoalEvent,
  buildIncidentEvent,
  buildSubstitutionEvent,
  HALF_TIME_LENGTH_MS,
  type Side,
  type HalfNumber,
} from "./events";
import { SINGLE_HALF_LENGTH_MS } from "./defaults";
import misconductCodes from "./netherlandsMisconductCodes.json";

export const RANDOM_MATCH_MISCONDUCT_CODE_ID = misconductCodes.id;

// Codes we pull `reason` from for random cards. Team-official codes are
// excluded — random match events are all attributed to a numbered player.
interface MisconductCode {
  code: string;
  reason: string;
  type: "yellow" | "red";
  teamOfficial?: boolean;
}
const playerCodes = (misconductCodes.codes as MisconductCode[]).filter(
  (c) => !c.teamOfficial
);
const yellowReasons = playerCodes
  .filter((c) => c.type === "yellow")
  .map((c) => c.reason);
const redReasons = playerCodes
  .filter((c) => c.type === "red")
  .map((c) => c.reason);

// Reusable single-shot randomisers — used both by planRandomMatch below and
// directly by the manual card buttons so they share the same randomisation
// behaviour (player 1-11, reason picked from the Netherlands codes list).
export function randomStarterNumber(rng: () => number = Math.random): number {
  return Math.floor(rng() * 11) + 1;
}
export function randomCardReason(
  card: "yellow" | "red",
  rng: () => number = Math.random
): string {
  const pool = card === "yellow" ? yellowReasons : redReasons;
  return pool[Math.floor(rng() * pool.length)];
}

export interface RandomMatchTimings {
  firstHalfKickOff: number;
  firstHalfEnd: number;
  halfTimeStart: number;
  halfTimeEnd: number;
  secondHalfKickOff: number;
  secondHalfEnd: number;
}

export interface PlannedEvent {
  title: string;
  payload: Record<string, unknown>;
}

export interface RandomMatchPlan {
  timings: RandomMatchTimings;
  events: PlannedEvent[];
}

export interface RandomMatchRanges {
  goals: [number, number];
  yellows: [number, number];
  reds: [number, number];
  subs: [number, number];
}

export const DEFAULT_RANGES: RandomMatchRanges = {
  goals: [0, 5],
  yellows: [1, 5],
  reds: [0, 2],
  subs: [0, 3],
};

export function buildRandomMatchTimings(
  firstHalfKickOff: number
): RandomMatchTimings {
  // Half boundaries get 1 ms gaps so strict ordering by timestamp is preserved
  // (same trick the live handleStartSecondHalf uses) — half time itself is a
  // realistic 15 min since this match is historical, not a skipped live one.
  const firstHalfEnd = firstHalfKickOff + SINGLE_HALF_LENGTH_MS;
  const halfTimeStart = firstHalfEnd + 1;
  const halfTimeEnd = halfTimeStart + HALF_TIME_LENGTH_MS;
  const secondHalfKickOff = halfTimeEnd + 1;
  const secondHalfEnd = secondHalfKickOff + SINGLE_HALF_LENGTH_MS;
  return {
    firstHalfKickOff,
    firstHalfEnd,
    halfTimeStart,
    halfTimeEnd,
    secondHalfKickOff,
    secondHalfEnd,
  };
}

export function planRandomMatch(
  firstHalfKickOff: number,
  ranges: RandomMatchRanges = DEFAULT_RANGES,
  rng: () => number = Math.random
): RandomMatchPlan {
  const timings = buildRandomMatchTimings(firstHalfKickOff);
  const randomInt = (min: number, max: number) =>
    Math.floor(rng() * (max - min + 1)) + min;
  const randomSide = (): Side => (rng() < 0.5 ? "home" : "away");
  const randomHalf = (): HalfNumber => (rng() < 0.5 ? 1 : 2);
  // Player numbers 1..11 are the on-pitch starters. #12 is the lone bench
  // player, reserved for sub-on so subs always swap a starter with the
  // single available substitute (the team only has 12 named players).
  const randomStarter = (): number => randomInt(1, 11);
  const pickReason = (pool: string[]): string =>
    pool[Math.floor(rng() * pool.length)];

  // Pick a timestamp strictly inside the chosen half (excludes the boundary
  // millisecond at each end so half-boundary events always sort first/last).
  const timestampInHalf = (half: HalfNumber): number => {
    const start = half === 1 ? timings.firstHalfKickOff : timings.secondHalfKickOff;
    const end = half === 1 ? timings.firstHalfEnd : timings.secondHalfEnd;
    const span = end - start - 2;
    return start + 1 + Math.floor(rng() * span);
  };

  interface InPlay {
    half: HalfNumber;
    timestamp: number;
    title: string;
    payload: Record<string, unknown>;
  }
  const inPlay: InPlay[] = [];

  const kickOffFor = (half: HalfNumber): number =>
    half === 1 ? timings.firstHalfKickOff : timings.secondHalfKickOff;

  const addEvents = (
    count: number,
    label: string,
    factory: (
      kickOff: number,
      side: Side,
      now: number,
      half: HalfNumber
    ) => Record<string, unknown>
  ): void => {
    for (let i = 0; i < count; i++) {
      const half = randomHalf();
      const side = randomSide();
      const timestamp = timestampInHalf(half);
      inPlay.push({
        half,
        timestamp,
        title: `Random ${label} (${side}, half ${half})`,
        payload: factory(kickOffFor(half), side, timestamp, half),
      });
    }
  };

  addEvents(
    randomInt(ranges.goals[0], ranges.goals[1]),
    "goal",
    (kickOff, side, now, half) =>
      buildGoalEvent(kickOff, side, now, half, randomStarter())
  );
  addEvents(
    randomInt(ranges.yellows[0], ranges.yellows[1]),
    "yellow card",
    (kickOff, side, now, half) =>
      buildIncidentEvent(
        kickOff,
        side,
        now,
        "yellow",
        half,
        randomStarter(),
        pickReason(yellowReasons)
      )
  );
  addEvents(
    randomInt(ranges.reds[0], ranges.reds[1]),
    "red card",
    (kickOff, side, now, half) =>
      buildIncidentEvent(
        kickOff,
        side,
        now,
        "red",
        half,
        randomStarter(),
        pickReason(redReasons)
      )
  );
  addEvents(
    randomInt(ranges.subs[0], ranges.subs[1]),
    "substitution",
    (kickOff, side, now, half) =>
      buildSubstitutionEvent(kickOff, side, now, half, randomStarter())
  );

  inPlay.sort((a, b) => a.timestamp - b.timestamp);
  const firstHalfPlay = inPlay.filter((e) => e.half === 1);
  const secondHalfPlay = inPlay.filter((e) => e.half === 2);

  const events: PlannedEvent[] = [];
  events.push({
    title: "Random match — 1st-half kickoff",
    payload: buildKickOffEvent(timings.firstHalfKickOff),
  });
  for (const e of firstHalfPlay) events.push({ title: e.title, payload: e.payload });
  events.push({
    title: "Random match — 1st-half end",
    payload: buildHalfEndEvent(timings.firstHalfKickOff, timings.firstHalfEnd),
  });
  events.push({
    title: "Random match — Half Time",
    payload: buildHalfTimeEvent(timings.halfTimeStart, timings.halfTimeEnd),
  });
  events.push({
    title: "Random match — 2nd-half kickoff",
    payload: buildSecondHalfKickOffEvent(timings.secondHalfKickOff),
  });
  for (const e of secondHalfPlay) events.push({ title: e.title, payload: e.payload });
  events.push({
    title: "Random match — Full Time",
    payload: buildSecondHalfEndEvent(
      timings.secondHalfKickOff,
      timings.secondHalfEnd
    ),
  });

  return { timings, events };
}
